/**
 * Header bar for the Warehouse Layout Editor.
 * Contains title, view mode toggle, save status, and action buttons.
 */

import React, { useRef } from 'react';
import {
  ArrowLeft,
  Save,
  Download,
  Upload,
  Settings,
  Loader2,
  CheckCircle,
  Undo2,
  Redo2,
  PersonStanding,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { ViewMode } from '../types/warehouseLayout';
import { exportLayoutAsJson, importLayoutFromJson } from '../utils/layoutStorage';

interface EditorHeaderProps {
  onBack?: () => void;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({ onBack }) => {
  const { state, dispatch, save, canUndo, canRedo } = useLayoutEditor();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { layout, editorState, saveStatus } = state;
  if (!layout) return null;

  const handleViewModeChange = (mode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', mode });
  };

  const handleExport = () => {
    exportLayoutAsJson(layout);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = await importLayoutFromJson(file);
      imported.warehouseId = layout.warehouseId;
      dispatch({ type: 'IMPORT_LAYOUT', layout: imported });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: err instanceof Error ? err.message : 'Import feilet' });
    }
    e.target.value = '';
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Tilbake"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">3D Lagervisning</h1>
            <p className="text-sm text-gray-500">
              {layout.name} &bull; {layout.racks.length} reoler plassert
              {editorState.selectedRacks.size > 0 && (
                <span className="text-blue-600 ml-2">
                  ({editorState.selectedRacks.size} valgt)
                </span>
              )}
            </p>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => handleViewModeChange('2d')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              editorState.viewMode === '2d'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            2D Editor
          </button>
          <button
            onClick={() => handleViewModeChange('3d')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              editorState.viewMode === '3d'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            3D Visning
          </button>
          <button
            onClick={() => handleViewModeChange('first-person')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
              editorState.viewMode === 'first-person'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title="Gå rundt i lageret (førsteperson)"
          >
            <PersonStanding className="w-4 h-4" />
            Førsteperson
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-500 flex items-center gap-1 mr-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Lagrer...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 flex items-center gap-1 mr-2">
              <CheckCircle className="w-4 h-4" />
              Lagret
            </span>
          )}

          <button
            onClick={() => dispatch({ type: 'UNDO' })}
            disabled={!canUndo}
            className={`p-2 rounded-lg ${canUndo ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
            title="Angre (Ctrl+Z)"
          >
            <Undo2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => dispatch({ type: 'REDO' })}
            disabled={!canRedo}
            className={`p-2 rounded-lg ${canRedo ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
            title="Gjenta (Ctrl+Shift+Z)"
          >
            <Redo2 className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          <button onClick={save} className="p-2 hover:bg-gray-100 rounded-lg" title="Lagre">
            <Save className="w-5 h-5" />
          </button>
          <button onClick={handleExport} className="p-2 hover:bg-gray-100 rounded-lg" title="Eksporter JSON">
            <Download className="w-5 h-5" />
          </button>
          <label className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer" title="Importer JSON">
            <Upload className="w-5 h-5" />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_SETTINGS' })}
            className={`p-2 rounded-lg ${state.showSettings ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            title="Innstillinger"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorHeader;
